import * as THREE from 'three';
import { PARTICLE_PLACEMENT, VISUAL_SETTINGS, normaliseWindMagnitudeRange } from '../constants.js';
import { generateBackgroundPlane } from './BackgroundPlaneUtil.js';
import {
  createParticlePositionTexture,
  generateParticleMesh,
  updateParticlePositions,
} from './utility/ParticleUtil.js';
import { generateRenderTarget } from './utility/RenderTargetUtil.js';
import { createClickHandler } from './onClickHandler.js';

export function loadWindsMode({
  scene,
  camera,
  renderer,
  controls,
  velocityTexture,
  enabled = true,
  windMagnitudeLimit,
  windMagnitudeRange,
  gradientTexture,
  planetMesh,
  onPointSelected,
  particleResolution = VISUAL_SETTINGS.particleResolution,
  particlePlacement = PARTICLE_PLACEMENT.visibleCap,
}) {
  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
  orthoCamera.position.z = 1;

  const backgroundScene = new THREE.Scene();
  const planetRadius = VISUAL_SETTINGS.planetRadius;
  const {
    particlePositionDataTexture,
    viewableSphereCapMidpoint,
    viewableSphereCapRadius,
  } = createParticlePositionTexture(camera, particleResolution, planetRadius, particlePlacement);

  const backgroundMesh = generateBackgroundPlane(
    velocityTexture,
    particlePositionDataTexture,
    viewableSphereCapMidpoint,
    viewableSphereCapRadius
  );
  const resolvedWindMagnitudeRange = normaliseWindMagnitudeRange(
    windMagnitudeRange ?? windMagnitudeLimit
  );
  const particleMesh = generateParticleMesh(
    particlePositionDataTexture,
    gradientTexture,
    particleResolution,
    velocityTexture,
    resolvedWindMagnitudeRange
  );
  const currentRT = generateRenderTarget(particleResolution);
  const previousRT = generateRenderTarget(particleResolution);

  particleMesh.name = 'particleMesh';
  scene.add(particleMesh);
  backgroundScene.add(backgroundMesh);

  const resources = {
    backgroundScene,
    orthoCamera,
    backgroundMesh,
    particleMesh,
    planetMesh,
    currentRT,
    previousRT,
    particlePositionDataTexture,
    velocityTexture,
    windMagnitudeLimit: resolvedWindMagnitudeRange.max,
    windMagnitudeRange: resolvedWindMagnitudeRange,
    gradientTexture,
    particleResolution,
    particlePlacement,
    particleParent: scene,
    enabled,
    needsRespawn: false,
    needsImmediateReset: false,
    interacting: false,
    pendingResetFrames: 0,
    lastRespawnAt: 0,
    domElement: renderer.domElement,
    controlsHandlers: null,
    onPointerDown: null,
  };

  particleMesh.visible = enabled;
  seedSimulation(resources, renderer);

  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  resources.onPointerDown = createClickHandler({
    camera,
    domElement: renderer.domElement,
    raycaster,
    mouse,
    planetMesh,
    getVelocityTexture: () => resources.velocityTexture,
    getWindMagnitudeRange: () => resources.windMagnitudeRange,
    onPointSelected,
  });

  resources.controlsHandlers = {
    start: () => {
      resources.interacting = true;
      resources.needsImmediateReset = true;
    },
    change: () => {
      resources.needsImmediateReset = true;
    },
    end: () => {
      resources.interacting = false;
      resources.needsImmediateReset = true;
    },
  };

  renderer.domElement.addEventListener('pointerdown', resources.onPointerDown, { passive: true });
  controls.addEventListener('start', resources.controlsHandlers.start);
  controls.addEventListener('change', resources.controlsHandlers.change);
  controls.addEventListener('end', resources.controlsHandlers.end);

  return resources;
}

function seedSimulation(resources, renderer) {
  resources.backgroundMesh.material.uniforms.u_positionTexture.value = resources.particlePositionDataTexture;

  renderer.setRenderTarget(resources.previousRT);
  renderer.render(resources.backgroundScene, resources.orthoCamera);
  renderer.setRenderTarget(null);

  [resources.currentRT, resources.previousRT] = [resources.previousRT, resources.currentRT];
  resources.particleMesh.material.uniforms.u_positionTexture.value = resources.currentRT.texture;
}

export function refreshWindsMode(resources, camera) {
  const nextState = updateParticlePositions(
    camera,
    resources.particleResolution,
    VISUAL_SETTINGS.planetRadius,
    resources.particleMesh,
    resources.particlePositionDataTexture,
    resources.backgroundMesh,
    resources.particlePlacement
  );

  resources.particlePositionDataTexture = nextState.particlePositionDataTexture;
  resources.needsRespawn = false;
  resources.pendingResetFrames = 12;
  resources.lastRespawnAt = performance.now();
}

export function resetWindsMode(resources, camera, renderer) {
  refreshWindsMode(resources, camera);
  resources.pendingResetFrames = 0;
  resources.backgroundMesh.material.uniforms.u_resetChance.value = VISUAL_SETTINGS.respawnResetChance;
  seedSimulation(resources, renderer);
}

export function setWindsQualityPreset(
  resources,
  camera,
  renderer,
  {
    particleResolution = resources.particleResolution,
    particlePlacement = resources.particlePlacement,
  } = {}
) {
  if (!resources) {
    return;
  }

  if (
    particleResolution === resources.particleResolution &&
    particlePlacement === resources.particlePlacement
  ) {
    resetWindsMode(resources, camera, renderer);
    return;
  }

  const parent = resources.particleMesh?.parent ?? resources.particleParent;
  const wasVisible = resources.particleMesh?.visible ?? resources.enabled;

  resources.currentRT?.dispose();
  resources.previousRT?.dispose();
  disposeMesh(resources.particleMesh, parent);

  const nextState = createParticlePositionTexture(
    camera,
    particleResolution,
    VISUAL_SETTINGS.planetRadius,
    particlePlacement
  );

  resources.particlePositionDataTexture?.dispose();
  resources.particlePositionDataTexture = nextState.particlePositionDataTexture;
  resources.backgroundMesh.material.uniforms.u_originalPositionTexture.value =
    nextState.particlePositionDataTexture;
  resources.backgroundMesh.material.uniforms.u_positionTexture.value =
    nextState.particlePositionDataTexture;
  resources.backgroundMesh.material.uniforms.u_viewableCircleRadius.value =
    nextState.viewableSphereCapRadius;
  resources.backgroundMesh.material.uniforms.u_viewableCircleMidpoint.value =
    nextState.viewableSphereCapMidpoint;
  resources.backgroundMesh.material.uniforms.u_resetChance.value =
    VISUAL_SETTINGS.respawnResetChance;

  resources.particleMesh = generateParticleMesh(
    nextState.particlePositionDataTexture,
    resources.gradientTexture,
    particleResolution,
    resources.velocityTexture,
    resources.windMagnitudeRange
  );
  resources.particleMesh.name = 'particleMesh';
  resources.particleMesh.visible = wasVisible && resources.enabled;
  resources.currentRT = generateRenderTarget(particleResolution);
  resources.previousRT = generateRenderTarget(particleResolution);
  resources.particleResolution = particleResolution;
  resources.particlePlacement = particlePlacement;
  resources.pendingResetFrames = 0;
  resources.needsRespawn = false;
  resources.needsImmediateReset = false;

  parent?.add(resources.particleMesh);
  seedSimulation(resources, renderer);
}

export function stepWindsMode(resources, renderer, dt) {
  const uniforms = resources.backgroundMesh.material.uniforms;

  if (resources.pendingResetFrames > 0) {
    resources.pendingResetFrames -= 1;

    if (resources.pendingResetFrames === 0) {
      uniforms.u_resetChance.value = VISUAL_SETTINGS.respawnResetChance;
    }
  }

  uniforms.u_delta.value = dt;
  uniforms.u_randomSeed.value = Math.random();
  uniforms.u_positionTexture.value = resources.currentRT.texture;

  renderer.setRenderTarget(resources.previousRT);
  renderer.render(resources.backgroundScene, resources.orthoCamera);
  renderer.setRenderTarget(null);

  [resources.currentRT, resources.previousRT] = [resources.previousRT, resources.currentRT];
  resources.particleMesh.material.uniforms.u_positionTexture.value = resources.currentRT.texture;
}

export function updateWindsVelocityTexture(resources, velocityTexture) {
  resources.velocityTexture = velocityTexture;
  resources.backgroundMesh.material.uniforms.u_velocityTexture.value = velocityTexture;
  resources.particleMesh.material.uniforms.u_velocityTexture.value = velocityTexture;
  resources.needsRespawn = true;
}

export function updateWindsVisualConfig(resources, { maxMagnitude, windMagnitudeRange } = {}) {
  const nextRange = normaliseWindMagnitudeRange(windMagnitudeRange ?? maxMagnitude);

  resources.windMagnitudeLimit = nextRange.max;
  resources.windMagnitudeRange = nextRange;
  resources.particleMesh.material.uniforms.u_minMagVelocity.value = nextRange.min;
  resources.particleMesh.material.uniforms.u_maxMagVelocity.value = nextRange.max;
}

export function updateWindsEnabled(resources, enabled, camera, renderer) {
  resources.enabled = enabled;
  resources.particleMesh.visible = enabled;

  if (enabled) {
    resources.needsImmediateReset = false;
    resetWindsMode(resources, camera, renderer);
    return;
  }

  resources.interacting = false;
  resources.needsRespawn = false;
  resources.needsImmediateReset = false;
}

export function disposeWindsMode(resources, controls) {
  if (!resources) {
    return;
  }

  resources.domElement?.removeEventListener('pointerdown', resources.onPointerDown);

  if (resources.controlsHandlers) {
    controls.removeEventListener('start', resources.controlsHandlers.start);
    controls.removeEventListener('change', resources.controlsHandlers.change);
    controls.removeEventListener('end', resources.controlsHandlers.end);
  }

  resources.currentRT?.dispose();
  resources.previousRT?.dispose();
  resources.particlePositionDataTexture?.dispose();

  disposeMesh(resources.particleMesh, resources.particleMesh?.parent);
  disposeMesh(resources.backgroundMesh, resources.backgroundMesh?.parent);
}

function disposeMesh(object, parent) {
  if (!object) {
    return;
  }

  parent?.remove(object);

  object.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
    }

    if (Array.isArray(child.material)) {
      child.material.forEach((material) => material.dispose());
    } else if (child.material) {
      child.material.dispose();
    }
  });
}
